import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import Header from '../../components/Header';
import AvatarFunc from '../../components/AvatarFunc';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../api';

const PAPEIS = [
  { v: 'colaborador', l: 'Colaborador' },
  { v: 'editor', l: 'Editor' },
  { v: 'ti', l: 'TI' },
  { v: 'rh', l: 'RH' },
  { v: 'admin', l: 'Admin' },
];
// TI, RH e Admin: só um administrador dá, tira ou reseta a senha (o backend barra também).
const PROTEGIDOS = ['ti', 'rh', 'admin'];
const SITUACOES = {
  nunca_entrou: { l: 'Nunca entrou', c: 'neutro' },
  senha_pendente: { l: 'Senha pendente', c: 'alerta' },
  ok: { l: 'Ativo', c: 'ok' },
};
const POR_PAGINA = 24;
const fmtData = (d) => (d ? new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : '');
const toast = (title) => Swal.fire({ toast: true, position: 'bottom-end', icon: 'success', title, timer: 2800, showConfirmButton: false });

export default function GestaoAcessos() {
  const { token, user, temPermissao } = useAuth();
  const [funcionarios, setFuncionarios] = useState([]);
  const [filiais, setFiliais] = useState([]);
  const [podeAdmin, setPodeAdmin] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [f, setF] = useState({ busca: '', filial: '', cargo: '', papel: '', situacao: '' });
  const [pagina, setPagina] = useState(1);

  useEffect(() => {
    api.get('/acessos')
      .then(({ data }) => { setFuncionarios(data.funcionarios); setFiliais(data.filiais || []); setPodeAdmin(!!data.pode_admin); })
      .catch(err => Swal.fire('Erro', err.response?.data?.error || 'Não foi possível carregar os colaboradores.', 'error'))
      .finally(() => setCarregando(false));
  }, []);

  const nomeFilial = (cod) => filiais.find(x => x.codigo === String(cod))?.nome || `Filial ${cod}`;
  const set = (campo, valor) => { setF(s => ({ ...s, [campo]: valor })); setPagina(1); };

  const cargos = useMemo(
    () => [...new Set(funcionarios.map(x => x.cargo).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [funcionarios]
  );

  const filtrados = useMemo(() => {
    const q = f.busca.trim().toLowerCase();
    return funcionarios.filter(p =>
      (!q || p.nome.toLowerCase().includes(q)) &&
      (!f.filial || String(p.filial) === f.filial) &&
      (!f.cargo || p.cargo === f.cargo) &&
      (!f.papel || p.papel === f.papel) &&
      (!f.situacao || p.situacao === f.situacao)
    );
  }, [funcionarios, f]);

  const totalPaginas = Math.max(1, Math.ceil(filtrados.length / POR_PAGINA));
  const paginaAtual = Math.min(pagina, totalPaginas);
  const fatia = filtrados.slice((paginaAtual - 1) * POR_PAGINA, paginaAtual * POR_PAGINA);
  const conta = (campo, valor) => funcionarios.filter(p => p[campo] === valor).length;

  // Mesma regra do backend: sem ser admin, não mexe em TI/RH/Admin (nem dando, nem tirando).
  const travado = (pessoa, papel) => !podeAdmin && (PROTEGIDOS.includes(papel) || PROTEGIDOS.includes(pessoa.papel));

  async function setPapel(pessoa, papel) {
    if (pessoa.id === user?.id) return Swal.fire('Opa', 'Você não pode alterar o seu próprio acesso.', 'info');
    if (travado(pessoa, papel)) return Swal.fire('Só administrador', 'Só um administrador pode dar ou tirar os papéis TI, RH e Admin.', 'info');
    if (PROTEGIDOS.includes(papel)) {
      const r = await Swal.fire({
        icon: 'warning',
        title: `Tornar ${pessoa.nome} ${PAPEIS.find(x => x.v === papel).l}?`,
        text: {
          admin: 'Admin tem acesso total, inclusive dar e tirar acesso de qualquer pessoa.',
          rh: 'RH edita todo o conteúdo, vê as atas de reunião, reseta senha de colaboradores, mapeia cargos e acompanha todo mundo.',
          ti: 'TI edita todo o conteúdo, mexe em colaboradores, cargos e organograma, acompanha todo mundo e vê o histórico de segurança — mas não vê as atas de reunião.',
        }[papel],
        showCancelButton: true, confirmButtonText: 'Sim, confirmar', cancelButtonText: 'Cancelar',
      });
      if (!r.isConfirmed) return;
    }
    try {
      await api.post(`/acessos/${pessoa.id}`, { papel });
      setFuncionarios(prev => prev.map(x => (x.id === pessoa.id ? { ...x, papel } : x)));
      toast(`Feito. Vale no próximo login de ${pessoa.nome.split(' ')[0]}.`);
    } catch (err) {
      Swal.fire('Ops', err.response?.data?.error || 'Não foi possível alterar o acesso.', 'error');
    }
  }

  async function resetarSenha(pessoa) {
    if (pessoa.id === user?.id) return Swal.fire('Opa', 'Para trocar a sua própria senha, use "Trocar senha" no menu.', 'info');
    if (travado(pessoa, pessoa.papel)) return Swal.fire('Só administrador', 'Só um administrador pode resetar a senha de quem é TI, RH ou Admin.', 'info');
    if (pessoa.situacao === 'nunca_entrou') {
      return Swal.fire('Nada pra resetar', `${pessoa.nome} ainda não entrou no portal — a senha já é os 6 primeiros dígitos do CPF.`, 'info');
    }
    const r = await Swal.fire({
      icon: 'warning', title: `Resetar a senha de ${pessoa.nome}?`,
      html: 'A senha volta a ser os <strong>6 primeiros dígitos do CPF</strong> e a pessoa será obrigada a definir uma nova no próximo acesso.',
      showCancelButton: true, confirmButtonText: 'Sim, resetar', cancelButtonText: 'Cancelar',
    });
    if (!r.isConfirmed) return;
    try {
      await api.post(`/acessos/${pessoa.id}/resetar-senha`);
      setFuncionarios(prev => prev.map(x => (x.id === pessoa.id ? { ...x, situacao: 'senha_pendente' } : x)));
      Swal.fire({ icon: 'success', title: 'Senha resetada', text: `${pessoa.nome} entra com os 6 primeiros dígitos do CPF e define a nova senha.` });
    } catch (err) {
      Swal.fire('Ops', err.response?.data?.error || 'Não foi possível resetar a senha.', 'error');
    }
  }

  return (
    <>
      <Header />
      <main className="conteudo-container" style={{ maxWidth: 1040 }}>
        <Link to="/admin" className="doc-voltar"><i className="bi bi-arrow-left"></i> Administração</Link>
        <div className="adm-topo">
          <div>
            <h1>Colaboradores e acessos</h1>
            <p>Todo funcionário ativo já é <strong>Colaborador</strong>. Aqui você vê quem já entrou, dá papéis extras e destrava senha.</p>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Link to="/admin/cargos" className="btn btn-outline-secondary"><i className="bi bi-diagram-2 me-1"></i> Cargos</Link>
            {(podeAdmin || temPermissao('ver_seguranca')) && <Link to="/admin/seguranca" className="btn btn-outline-secondary"><i className="bi bi-shield-check me-1"></i> Histórico</Link>}
          </div>
        </div>

        {!carregando && (
          <div className="equipe-resumo" style={{ marginBottom: 12 }}>
            <div className="equipe-stat"><span className="equipe-stat-num">{funcionarios.length}</span> ativos</div>
            <div className="equipe-stat equipe-stat--ok"><span className="equipe-stat-num">{conta('situacao', 'ok')}</span> já usam</div>
            <div className="equipe-stat equipe-stat--alerta"><span className="equipe-stat-num">{conta('situacao', 'nunca_entrou')}</span> nunca entraram</div>
            <div className="equipe-stat"><span className="equipe-stat-num">{conta('situacao', 'senha_pendente')}</span> com senha pendente</div>
          </div>
        )}

        <div className="acesso-legenda">
          <span><b className="lg-colab">Colaborador</b> — lê cultura, organograma, avisos e documentos, e faz as trilhas.</span>
          <span><b className="lg-editor">Editor</b> — cria e edita documentos, trilhas, quizzes e avisos.</span>
          <span><b className="lg-ti">TI</b> — tudo do Editor + reseta senha, dá papel de Editor, cargos e organograma, acompanha todos e vê o histórico de segurança. <strong>Não vê as atas de reunião.</strong></span>
          <span><b className="lg-rh">RH</b> — tudo do Editor + atas de reunião, reseta senha, dá papel de Editor, mapeia cargos e acompanha todos.</span>
          <span><b className="lg-admin">Admin</b> — tudo, inclusive dar TI/RH/Admin e ver o histórico de segurança.</span>
          <span className="adm-dica">O papel <strong>soma</strong> com o cargo: ninguém perde o que o próprio cargo já dá.</span>
        </div>

        <div className="acesso-filtros">
          <div className="doc-busca" style={{ flex: 2, marginBottom: 0 }}>
            <i className="bi bi-search"></i>
            <input type="text" placeholder="Buscar por nome…" value={f.busca} onChange={e => set('busca', e.target.value)} />
            {f.busca && <button type="button" className="doc-busca-limpar" onClick={() => set('busca', '')}>×</button>}
          </div>
          <select className="doc-input" style={{ flex: 1 }} value={f.filial} onChange={e => set('filial', e.target.value)}>
            <option value="">Todas as filiais</option>
            {filiais.map(x => <option key={x.codigo} value={x.codigo}>{x.nome}</option>)}
          </select>
          <select className="doc-input" style={{ flex: 1 }} value={f.cargo} onChange={e => set('cargo', e.target.value)}>
            <option value="">Todos os cargos</option>
            {cargos.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select className="doc-input" style={{ flex: 1 }} value={f.papel} onChange={e => set('papel', e.target.value)}>
            <option value="">Todos os papéis</option>
            {PAPEIS.map(p => <option key={p.v} value={p.v}>{p.l}</option>)}
          </select>
          <select className="doc-input" style={{ flex: 1 }} value={f.situacao} onChange={e => set('situacao', e.target.value)}>
            <option value="">Qualquer situação</option>
            <option value="nunca_entrou">Nunca entrou</option>
            <option value="senha_pendente">Senha pendente</option>
            <option value="ok">Ativo</option>
          </select>
        </div>

        {carregando && <div>Carregando…</div>}
        {!carregando && <div className="acesso-contagem">{filtrados.length} funcionário(s)</div>}

        <div className="acesso-lista">
          {fatia.map(p => {
            const sit = SITUACOES[p.situacao] || SITUACOES.ok;
            const eu = p.id === user?.id;
            return (
              <div className="acesso-row" key={p.id}>
                <div className="acesso-info">
                  <AvatarFunc id={p.id} nome={p.nome} temFoto={p.tem_foto} token={token} size={42} />
                  <div className="acesso-info-txt">
                    <div className="acesso-nome">{p.nome}{eu && <span className="acesso-voce"> (você)</span>}</div>
                    <div className="acesso-sub">
                      {p.cargo || 'sem cargo'} · {nomeFilial(p.filial)}{' '}
                      <span className={`adm-badge adm-badge--${sit.c}`}>
                        {sit.l}{p.situacao !== 'nunca_entrou' && p.ultimo_login ? ` · ${fmtData(p.ultimo_login)}` : ''}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="acesso-acoes">
                  <div className="acesso-papeis">
                    {PAPEIS.map(pp => {
                      const trava = !eu && p.papel !== pp.v && travado(p, pp.v);
                      return (
                        <button
                          key={pp.v}
                          disabled={trava}
                          title={trava ? 'Só um administrador mexe nos papéis RH e Admin' : undefined}
                          className={`acesso-papel-btn${p.papel === pp.v ? ' acesso-papel-btn--on acesso-papel-btn--' + pp.v : ''}`}
                          onClick={() => p.papel !== pp.v && setPapel(p, pp.v)}
                        >
                          {pp.l}
                        </button>
                      );
                    })}
                  </div>
                  {!eu && (podeAdmin || !PROTEGIDOS.includes(p.papel)) && (
                    <button className="acesso-reset-btn" onClick={() => resetarSenha(p)} title="Resetar senha (volta para os 6 primeiros dígitos do CPF)">
                      <i className="bi bi-key"></i>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {!carregando && filtrados.length === 0 && (
          <div className="conteudo-card adm-vazio">Nenhum funcionário com esses filtros.</div>
        )}

        {totalPaginas > 1 && (
          <div className="acesso-paginacao">
            <button disabled={paginaAtual === 1} onClick={() => setPagina(paginaAtual - 1)}><i className="bi bi-chevron-left"></i></button>
            <span>Página {paginaAtual} de {totalPaginas}</span>
            <button disabled={paginaAtual === totalPaginas} onClick={() => setPagina(paginaAtual + 1)}><i className="bi bi-chevron-right"></i></button>
          </div>
        )}
      </main>
    </>
  );
}
